import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { AdminService } from 'src/app/shared/services/admin.service';
import { ProjectManagementService } from 'src/app/shared/services/project-management.service';
import { ApproveUserModalComponent } from './modal/approve-user-modal/approve-user-modal.component';
import { width40, zIndex10000, ALLOWED_DASHBOARD_EMAILS } from 'src/app/shared/global.constant';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  isAuthorizedAdmin: boolean = false;

  currentUsers: any[] = [];
  pendingUsers: any[] = [];

  recentProjects: any[] = [];       // full, unfiltered list from API
  filteredProjects: any[] = [];     // list after applying the year filter — used by table + charts
  totalProjects: number = 0;

  availableYears: number[] = [];
  selectedYear: number | 'all' = 'all';
  yearOptions: { label: string; value: number | 'all' }[] = [];

  installationTypeChartData: any;
  monthlyChartData: any;
  chartOptions: any;

  userType: string = sessionStorage.getItem('userType') as string;
  userId: number = Number(sessionStorage.getItem('userId'));
  isGettingData: boolean = false;

  constructor(
    private adminService: AdminService,
    private projectManagementService: ProjectManagementService,
    private router: Router,
    public dialogService: DialogService
  ) {
    this.chartOptions = {
      plugins: { legend: { labels: { color: '#495057' } } },
      scales: {
        x: { ticks: { color: '#6b6b6b' } },
        y: { ticks: { color: '#6b6b6b' } }
      }
    };
  }

  ngOnInit(): void {
    const userEmail = sessionStorage.getItem('userEmail');
    this.isAuthorizedAdmin = ALLOWED_DASHBOARD_EMAILS.includes(userEmail as string);
    if (this.isAuthorizedAdmin) {
      this.loadCountryDashboard();
    }
    this.loadMyProjectsSummary();
  }

  loadCountryDashboard(): void {
    this.isGettingData = true;
    this.adminService.getCountryDashboard(this.userId, 'Australia').subscribe({
      next: (data) => {
        this.isGettingData = false;
        if (data && data.isSuccess) {
          this.currentUsers = data.countryDashboardResponseDataVM?.currentUsers || [];
          this.pendingUsers = data.countryDashboardResponseDataVM?.pendingUsers || [];
        }
      },
      error: () => { this.isGettingData = false; }
    });
  }

  loadMyProjectsSummary(): void {
    this.isGettingData = true;
    this.projectManagementService
      .getProjectDetails(this.userType, this.userId, 'myprojects', 1, '')
      .subscribe({
        next: (data: any) => {
          this.isGettingData = false;
          if (data && data.isSuccess) {
            this.recentProjects = data.projectSearchResponseDataVM?.searchResultDataVM || [];
            this.buildYearOptions();
            this.selectedYear = 'all';
            this.applyYearFilter();
          }
        },
        error: () => { this.isGettingData = false; }
      });
  }

  /** Derives the distinct list of years present in the data, newest first. */
  private buildYearOptions(): void {
    const yearsSet = new Set<number>();
    this.recentProjects.forEach((p) => {
      const parsed = this.parseDdMmYyyy(p.CREATED_DATE);
      if (parsed) {
        yearsSet.add(parsed.getFullYear());
      }
    });

    this.availableYears = Array.from(yearsSet).sort((a, b) => b - a);
    this.yearOptions = [
      { label: 'All Years', value: 'all' },
      ...this.availableYears.map((y) => ({ label: String(y), value: y }))
    ];
  }

  /** Called from the template when the year dropdown changes. */
  onYearChange(): void {
    this.applyYearFilter();
  }

  private applyYearFilter(): void {
    this.filteredProjects = this.selectedYear === 'all'
      ? this.recentProjects
      : this.recentProjects.filter((p) => {
          const parsed = this.parseDdMmYyyy(p.CREATED_DATE);
          return parsed?.getFullYear() === this.selectedYear;
        });

    this.totalProjects = this.filteredProjects.length;
    this.buildCharts();
  }

  private buildCharts(): void {
    if (this.filteredProjects.length === 0) {
      this.installationTypeChartData = null;
      this.monthlyChartData = null;
      return;
    }

    const typeCounts: { [key: string]: number } = {};
    this.filteredProjects.forEach((p) => {
      const type = p.INSTALLATION_TYPE || 'Unspecified';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    this.installationTypeChartData = {
      labels: Object.keys(typeCounts),
      datasets: [{
        data: Object.values(typeCounts),
        backgroundColor: ['#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#e53935', '#00acc1'],
        hoverBackgroundColor: ['#1976d2', '#388e3c', '#ef6c00', '#7b1fa2', '#c62828', '#00838f']
      }]
    };

    const monthCounts: { [key: string]: number } = {};
    const monthKeys: { [key: string]: Date } = {};

    this.filteredProjects.forEach((p) => {
      const label = this.parseMonthLabel(p.CREATED_DATE);
      monthCounts[label] = (monthCounts[label] || 0) + 1;
      if (!monthKeys[label]) {
        monthKeys[label] = this.parseDdMmYyyy(p.CREATED_DATE) || new Date(0);
      }
    });

    const sortedLabels = Object.keys(monthCounts).sort(
      (a, b) => monthKeys[a].getTime() - monthKeys[b].getTime()
    );

    this.monthlyChartData = {
      labels: sortedLabels,
      datasets: [{
        label: 'Projects Created',
        data: sortedLabels.map((label) => monthCounts[label]),
        backgroundColor: '#1e88e5'
      }]
    };
  }

  /** Parses a "DD/MM/YYYY" string into a Date, or null if malformed. */
  private parseDdMmYyyy(rawDate: string): Date | null {
    if (!rawDate) return null;
    const parts = rawDate.split('/');
    if (parts.length !== 3) return null;

    const day = Number(parts[0]);
    const month = Number(parts[1]);
    const year = Number(parts[2]);
    if (!day || !month || !year) return null;

    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private parseMonthLabel(rawDate: string): string {
    const parsed = this.parseDdMmYyyy(rawDate);
    return parsed
      ? parsed.toLocaleString('default', { month: 'short', year: 'numeric' })
      : 'Unknown';
  }

  approve(user: any): void {
    const dialogRef = this.dialogService.open(ApproveUserModalComponent, {
      header: 'Approve User',
      width: width40,
      baseZIndex: zIndex10000,
      closable: true,
      data: {
        selectedUserId: user.id,
        selectedUserName: user.name
      }
    });

    dialogRef.onClose.subscribe((result: any) => {
      if (result && result.refreshTable) {
        this.loadCountryDashboard();
      }
    });
  }

  viewProject(project: any): void {
    this.router.navigate(['/project-overview'], {
      queryParams: {
        projectId: btoa(project.PROJECT_ID.toLocaleString()),
        userId: btoa(project.USER_ID.toLocaleString()),
      },
    });
  }
}